import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { greyText, lightGreys } from "../../../styles";
import { CompassParagraph } from "../../../components/common/styledComponents";
import {
  ElementContainer,
  RowContainer,
  VerticalLine,
} from "../../../components/containers/styledComponents";
import {
  Icon,
  IconContainer,
} from "../../../components/visuals/styledComponents";
import { ENGAGEMENT_META_THEME_IMAGES } from "../constants";
import { ToggleAccordionBlueText } from "../../../components/buttons/ToggleAccordionBlueText";
import { EngagementType } from "../../../models/stewardshipModels";
import { Tooltip } from "antd";
import { MESSAGES } from "../../../messages";

const BadgeCardWrapper = styled.div<{ isExpanded?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: ${({ isExpanded }): string => (isExpanded ? "1200px" : "auto")};
  padding: 8px;
  flex-shrink: 0;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const InlineParagraph = styled(CompassParagraph)<{ isExpanded: boolean }>`
  width: auto;
  max-width: 200px;
  display: inline;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

type BadgeCardProps = {
  title: string;
  subtitle: string;
  isMapped: boolean;
  type: EngagementType;
  topics: string[];
  description?: string;
};

export const BadgeCard = ({
  title,
  subtitle,
  isMapped,
  type,
  description,
}: BadgeCardProps): JSX.Element => {
  const cardRef = useRef<HTMLDivElement>(null);

  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (isExpanded && cardRef.current) {
      const container = cardRef.current.parentElement;
      if (container) {
        const x = cardRef.current.offsetLeft - 400;
        container.scrollTo({
          left: Math.max(0, x),
          behavior: "smooth",
        });
      }
    }
  }, [isExpanded]);

  return (
    <Container ref={cardRef}>
      <BadgeCardWrapper isExpanded={isExpanded}>
        <ElementContainer ai="flex-start">
          <IconContainer p={1} m={1}>
            <Icon
              src={
                ENGAGEMENT_META_THEME_IMAGES[type][
                  isMapped ? "logo" : "disabledLogo"
                ]
              }
            />
          </IconContainer>
          <ElementContainer ml={10} flexDirection="column" ai="flex-start">
            <Tooltip title={title}>
              <InlineParagraph fontSize={14} isExpanded={isExpanded}>
                {title}
              </InlineParagraph>
            </Tooltip>
            <Tooltip title={subtitle}>
              <InlineParagraph
                fontSize={11}
                color={greyText}
                mt={4}
                mb={4}
                isExpanded={isExpanded}
              >
                {subtitle}
              </InlineParagraph>
            </Tooltip>
            <ToggleAccordionBlueText
              textActive="Show Description"
              textInactive="Hide Description"
              handleChange={setIsExpanded}
            />
          </ElementContainer>
        </ElementContainer>
        <VerticalLine height={36} color={lightGreys.borderLightGrey} />
      </BadgeCardWrapper>
      {isExpanded && (
        <RowContainer>
          <ElementContainer p={8}>
            <CompassParagraph fontSize={12} color={greyText} ta="justify">
              {description || MESSAGES.NOT_AVAILABLE}
            </CompassParagraph>
          </ElementContainer>
        </RowContainer>
      )}
    </Container>
  );
};
